/**
 * Required fields — what a request needs before it can be screened, what was
 * found for each, and where each value came from.
 *
 * This replaces two sections that answered half the question each. "Source
 * Traceability" listed every field the parser happened to map, in parser
 * vocabulary, with no notion of which ones matter. "Extracted Data" showed a
 * fixed nine values whose membership had nothing to do with the required set —
 * it carried Cap Rate and Occupancy, which are not required, and omitted Rate
 * Type, Lien Position, Payment Frequency and the rest, which are.
 *
 * So neither could answer the question an operator actually has: *can this be
 * screened, and if not, what is missing?* One list, driven by the catalogue's
 * own `required` flag, answers it — and missing required fields lead, because
 * they are the ones that need an action.
 */

import { useMemo } from 'react'
import { isYearField } from '@/components/deal/dossier/format'
import { AlertTriangle, CheckCircle2, Link2 } from 'lucide-react'

import type { DossierCoverage, DossierField, SourceAnchor, SourceFileRef } from '@/services/ingestion'
import { FieldStateBadge, type FieldState } from '@/components/fields/fieldState'
import { confirmedText } from '@/components/fields/lineage'

const STATE_OF: Record<string, FieldState> = {
  stated: 'stated',
  confirmed: 'confirmed',
  derived: 'derived',
  suggested: 'suggested',
  conflict: 'conflicted',
  missing: 'missing',
  in_unread_model: 'in_unread_model',
  not_derivable_from_package: 'not_applicable',
}

function displayValue(field: DossierField): string {
  const v = field.value
  if (v === null || v === undefined || v === '') {
    if (field.gapKind === 'desk_assigned' || field.gapKind === 'external') return 'Needs manual'
    if (field.gapKind === 'unread_attachment') return 'In unread attachment'
    if (field.status === 'not_derivable_from_package') return 'N/A'
    return 'Not found'
  }
  if (typeof v === 'number') {
    if (isYearField(field.field)) return String(v)
    if (field.kind === 'currency') {
      return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    }
    if (field.kind === 'percent') {
      return `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
    }
    return v.toLocaleString()
  }
  return String(v)
}

/** The place in the source a value was read from, as the viewer takes it. Null
 *  when the dossier recorded no place (a value a person typed, a derivation). */
function anchorOf(field: DossierField): SourceAnchor | null {
  const p = field.provenance
  const loc = p?.locator
  const lineNo = loc?.lineNo ?? (p?.lineNo != null && p.lineNo !== '' ? Number(p.lineNo) : null)
  const page = loc?.page ?? undefined
  const cell = loc?.cell ?? p?.cellRef ?? undefined
  if (lineNo == null && page == null && !cell) return null
  return {
    ...(loc?.kind ? { kind: loc.kind } : {}),
    ...(lineNo != null && Number.isFinite(lineNo) ? { lineNo } : {}),
    ...(page != null ? { page } : {}),
    ...(cell ? { cell } : {}),
    ...(loc?.region ? { region: loc.region } : {}),
    ...(p?.sourceSha256 ? { sha256: p.sourceSha256 } : {}),
  }
}

/** A found value, or nothing. A suggestion is not found: it is a default the
 *  platform offers, and it is not data until a person confirms it. */
function isFound(field: DossierField): boolean {
  return (
    field.status !== 'missing' &&
    field.status !== 'suggested' &&
    field.value !== null &&
    field.value !== undefined &&
    field.value !== ''
  )
}

export type RequiredFieldsPanelProps = {
  coverage: DossierCoverage | undefined
  loading?: boolean
  /** Open a source document at the place a value was read from: the workbook
   *  cell, and the page and line the dossier recorded for a document or email. */
  onOpenSource?: (source: string, cellRef?: string | null, anchor?: SourceAnchor | null) => void
  sourceFiles?: SourceFileRef[]
  onHoverSource?: (sha256: string | null) => void
}

export function RequiredFieldsPanel({
  coverage,
  loading,
  onOpenSource,
  sourceFiles = [],
  onHoverSource,
}: RequiredFieldsPanelProps) {
  const required = useMemo(() => {
    const rows = (coverage?.groups ?? []).flatMap((group) => group.fields.filter((f) => f.required))
    // Missing first: those are the ones that need an action. Within each half,
    // catalogue order, which is the order the screening form asks in.
    return [...rows].sort((a, b) => Number(isFound(a)) - Number(isFound(b)))
  }, [coverage])

  const found = required.filter(isFound).length
  const total = required.length
  const shortfall = total - found

  if (loading) {
    return <p className="text-[11px] text-stone-500">Reading the package…</p>
  }
  if (total === 0) {
    return (
      <p className="text-[11px] text-stone-500">
        No required fields are defined for this record yet.
      </p>
    )
  }

  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5">
        {shortfall === 0 ? (
          <CheckCircle2 size={11} className="text-emerald-400" />
        ) : (
          <AlertTriangle size={11} className="text-amber-400" />
        )}
        <span className="text-[10px] font-mono uppercase tracking-widest text-stone-500">
          Required fields
        </span>
        <span
          className={`ml-auto inline-flex items-center rounded border px-2 py-0.5 text-[9px] font-medium uppercase tracking-wide ${
            shortfall === 0
              ? 'border-emerald-400/25 bg-emerald-400/10 text-emerald-300'
              : 'border-amber-400/25 bg-amber-400/10 text-amber-300'
          }`}
        >
          {shortfall === 0 ? `All ${total} found` : `${shortfall} of ${total} still needed`}
        </span>
      </div>

      {shortfall > 0 ? (
        <p className="mb-2 text-[10px] leading-relaxed text-stone-500">
          A request cannot be screened until every field below carries a value.
          The ones without one are listed first.
        </p>
      ) : null}

      <div className="divide-y divide-white/[0.04] rounded border border-white/[0.06]">
        {required.map((field) => {
          const has = isFound(field)
          const source = field.provenance?.sourceDoc ?? null
          const target = source
            ? sourceFiles.find((f) => f.filename === source)
              ?? (source.startsWith('email') ? sourceFiles.find((f) => f.kind === 'email_source') : undefined)
            : undefined
          return (
            <div
              key={field.field}
              className={`flex items-center gap-2 px-2.5 py-1.5 text-[11px] ${
                has ? '' : 'bg-amber-400/[0.03]'
              }`}
            >
              <span className="w-[150px] shrink-0 truncate text-stone-500" title={field.label}>
                {field.label}
              </span>
              <span
                className={`min-w-0 flex-1 truncate font-medium ${
                  has ? 'text-stone-200' : 'text-amber-300/70 italic'
                }`}
                title={displayValue(field)}
              >
                {displayValue(field)}
              </span>

              {/* Where it came from. A required value with no source is a value
                  someone typed, and that is worth seeing at a glance. */}
              {source ? (
                <button
                  type="button"
                  onClick={() => onOpenSource?.(source, field.provenance?.cellRef, anchorOf(field))}
                  onMouseEnter={() => onHoverSource?.(target?.sha256 ?? null)}
                  onMouseLeave={() => onHoverSource?.(null)}
                  className="inline-flex shrink-0 items-center gap-1 rounded border border-stone-700 bg-stone-800/60 px-1.5 py-0.5 text-[8px] text-stone-400 transition hover:border-amber-400/50 hover:text-amber-300"
                  title={`Read from ${source}${field.provenance?.cellRef ? ` [${field.provenance.cellRef}]` : ''} — click to open and highlight`}
                >
                  <Link2 size={8} />
                  {source.startsWith('email') ? 'email' : source.length > 18 ? `${source.slice(0, 16)}…` : source}
                  {field.provenance?.cellRef ? ` [${field.provenance.cellRef}]` : ''}
                </button>
              ) : field.status === 'confirmed' ? (
                <span
                  className="shrink-0 truncate text-[8px] uppercase tracking-wide text-stone-500"
                  title={confirmedText(field.confirmation)}
                >
                  {field.confirmation?.by ?? 'reviewer'}
                </span>
              ) : field.status === 'suggested' ? (
                <span
                  className="shrink-0 truncate text-[8px] uppercase tracking-wide text-stone-500"
                  title={field.basis ?? field.note ?? undefined}
                >
                  assumption
                </span>
              ) : has ? (
                <span className="shrink-0 text-[8px] uppercase tracking-wide text-stone-600">
                  no source
                </span>
              ) : null}

              {/* Stated means a document said it. A value with no source is a
                  suggestion until a person confirms it (INT-18). */}
              <FieldStateBadge
                state={
                  field.status === 'stated' && !source
                    ? 'suggested'
                    : field.gapKind === 'unread_attachment'
                      // The value reads 'In unread attachment'; the badge
                      // says the same, not 'Missing' (dossierui-13).
                      ? 'in_unread_attachment'
                      : STATE_OF[field.status] ?? 'missing'
                }
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
